/* Shared rules for customer advance amounts, so the single field and the
   per job list accept and reject exactly the same input. */

/* Digits with at most two decimals, to match the currency. */
export function limitToTwoDecimals(value) {
  const cleaned = String(value == null ? "" : value).replace(/[^\d.]/g, "");
  const firstDot = cleaned.indexOf(".");
  if (firstDot === -1) return cleaned;
  return (
    cleaned.slice(0, firstDot + 1) +
    cleaned.slice(firstDot + 1).replace(/\./g, "").slice(0, 2)
  );
}

/* Rejects a keystroke that would break the format, so nothing invalid ever
   reaches the field. */
export function blockInvalidAmountKey(e) {
  const char = e.key || String.fromCharCode(e.charCode);
  if (!char || char.length !== 1) return;
  const el = e.target;
  const value = String(el.value == null ? "" : el.value);
  const next =
    value.slice(0, el.selectionStart) + char + value.slice(el.selectionEnd);
  if (!/^\d*\.?\d{0,2}$/.test(next)) e.preventDefault();
}

/* null when blank, NaN when it is not a usable number. */
export function parseAmount(value) {
  const raw = value === null || value === undefined ? "" : String(value).trim();
  if (!raw) return null;
  const parsed = Number(raw);
  if (isNaN(parsed) || parsed < 0) return NaN;
  return parsed;
}

/* A ceiling of null, 0 or anything unparseable means uncapped. */
export function normaliseMax(maxAmount) {
  const parsed = Number(maxAmount);
  if (
    maxAmount === null ||
    maxAmount === undefined ||
    maxAmount === "" ||
    isNaN(parsed)
  ) {
    return null;
  }
  return parsed > 0 ? parsed : null;
}

export function formatAmount(amount, currency) {
  if (amount === null || amount === undefined) return "";
  return `${Number(amount).toFixed(2)}${currency ? " " + currency : ""}`;
}

/* The message to show, or "" when the amount is acceptable. Zero is allowed:
   sending 0 for a job that has an advance is how it gets cleared. */
export function validateAmount({
  value,
  max,
  maxLabel = "Customer Amount",
  currency = "",
  required = true,
}) {
  const amount = parseAmount(value);
  if (amount === null) {
    return required ? "Please enter the Advance Amount." : "";
  }
  if (isNaN(amount)) return "Please enter a valid amount.";
  const ceiling = normaliseMax(max);
  if (ceiling !== null && amount > ceiling) {
    return `Advance amount cannot be greater than the ${maxLabel} (${formatAmount(
      ceiling,
      currency
    )}).`;
  }
  return "";
}

/* Editing an advance on a combined invoice rebuilds the one shared PDF and
   saves its new URL on every job of that invoice, which the response names in
   invoiceJobIds. Patching the rows in place keeps their download links right
   even while the list endpoint still serves a cached response.
   `getJobId` reads the job id from a row, since lists hold different shapes. */
export function applyRebuiltInvoice(rows, invoice, getJobId) {
  if (!rows || !invoice || !invoice.invoicePdfUrl) return;
  const ids = invoice.invoiceJobIds || [invoice.jobId];
  rows.forEach((row) => {
    if (ids.indexOf(getJobId(row)) === -1) return;
    row.invoicePdfUrl = invoice.invoicePdfUrl;
    if (invoice.invoiceNumber) row.invoiceNumber = invoice.invoiceNumber;
  });
}

/* These endpoints answer a 400 with a message naming the job and the limit,
   which is more use than anything we could write here. */
export function apiErrorMessage(error, fallback) {
  const message =
    error && error.response && error.response.data
      ? error.response.data.message
      : null;
  return message || fallback;
}
